import {customElement, html as _, LitElement, property} from "lit-element";
import {NAME_SPACE} from "@kta20-core/constants";
import {CustomFilterByType, FilterItemTyped} from "@kta20-ui-base/comps/models/filter-item";
import {
    CustomFilterSelectionType,
    IUiSearchFilterComponent
} from "@kta20-ui-base/comps/interfaces/i-ui-search-filter-component";

const filterByFreeTextTmpl=(uiSearchFilterComp:UiSearchFilterComponent)=>_`
        <ion-item lines="none">
            <ion-searchbar 
                placeholder="${uiSearchFilterComp.searchFilterPlaceholder + uiSearchFilterComp.customFilterSelectedValues[0].label.toLowerCase()}" 
                inputmode="text"
                type="text"
                debounce="750"
                @ionChange="${(event:CustomEvent)=>{uiSearchFilterComp.onFilterChanged(event.detail.value, CustomFilterByType.TEXT_TYPE)}}">
            </ion-searchbar>
        </ion-item>
`;

const filterByNumberTmpl=(uiSearchFilterComp:UiSearchFilterComponent)=>_`
        <ion-item lines="none">
            <ion-searchbar 
                placeholder="${uiSearchFilterComp.searchFilterPlaceholder}" 
                inputmode="number"
                type="number"
                debounce="750"
                @ionChange="${(event:CustomEvent)=>{uiSearchFilterComp.onFilterChanged(event.detail.value, CustomFilterByType.NUMBER_TYPE)}}">
            </ion-searchbar>
        </ion-item>
`;

const filterByDateTmpl=(uiSearchFilterComp:UiSearchFilterComponent, item:FilterItemTyped)=>_`
        <ion-item lines="none">
            <ion-label>${item.label}</ion-label>
            <ion-datetime 
                hour-values="0"
                minute-values="0"
                display-format="${uiSearchFilterComp.customFilterByDateFormat}" 
                placeholder="${uiSearchFilterComp.customFilterByDatePlaceholder}"
                @ionChange="${(event:CustomEvent)=>{uiSearchFilterComp.onDateFilterChanged(event.detail.value, item.type)}}">
            </ion-datetime>
        </ion-item>
`;

const filterByToggleTmpl=(uiSearchFilterComp:UiSearchFilterComponent, item:FilterItemTyped)=>_`
        <ion-item lines="none" id="ion-toggle">
            <ion-label>${item.label}: ${(item.inputValue) ? item.statusOn : item.statusOff}</ion-label>
            <ion-toggle 
                slot="end" 
                value="${item.value}"
                @ionChange="${(event:CustomEvent)=>{uiSearchFilterComp.onToggleFilterChanged(event.detail.checked, item)}}">
            </ion-toggle>
        </ion-item>
`;

@customElement(UiSearchFilterComponent.TAG_NAME)
export class UiSearchFilterComponent extends LitElement implements IUiSearchFilterComponent {

    static TAG_NAME = 'kta20-ui-search-filter';

    static CUSTOM_FILTER_ALL_LABEL = '**';
    static CUSTOM_FILTER_ALL_VALUE = 'all';

    private readonly customFilterSelectionType: CustomFilterSelectionType;

    @property({type: String})
    searchFilterPlaceholder = '[search]';

    @property({type: String})
    customFilterTitle = '[filter by]';

    @property({type: Array<FilterItemTyped>()})
    customFilterSelectedValues = [];

    @property({type: Array})
    customFilterItems: Array<FilterItemTyped>;

    @property({type: String})
    customFilterConfirmLabel = '[ok]';

    @property({type: String})
    customFilterCancelLabel = '[cancel]';

    @property({type: String})
    customFilterByDatePlaceholder = '[select date]';

    @property({type: String})
    customFilterByDateFormat = 'MM-DD-YYYY';

    readonly renderRoot: Element | ShadowRoot;

    constructor(useShadow: boolean = false,
    ) {
        super();
        this.renderRoot = useShadow ? this.shadowRoot : this;
        this.customFilterSelectionType = CustomFilterSelectionType.SINGLE_CHOISE;
    }

    async connectedCallback() {
        super.connectedCallback();

        if(this.customFilterSelectionType === CustomFilterSelectionType.SINGLE_CHOISE && this.customFilterItems.length > 0) this.customFilterSelectedValues.push(this.customFilterItems[0]);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    async onCustumerFilter() {
        const alert = this.getAlert();

        alert.header = this.customFilterTitle;
        alert.inputs = this.createAlertInputs();
        alert.buttons = [
            {
                text: this.customFilterCancelLabel,
                role: 'cancel',
                cssClass: 'secondary',
                handler: () => {
                    console.log(`${NAME_SPACE}: Confirm Cancel`);
                }
            }, {
                text: this.customFilterConfirmLabel,
                handler: (data) => {
                    this.setFilterTypeSelected(data);
                    console.log(`${NAME_SPACE}: set ${data} in filter list`);
                }
            }
        ];
        document.body.appendChild(alert);

        return alert.present();
    }

    onFilterChanged(value: any, type:CustomFilterByType) {
        if(type) {
            const customFilterSelected=this.customFilterSelectedValues.find(elm => elm.type===type);
            if(customFilterSelected) {
                customFilterSelected.inputValue = value;
            }
            else {
                this.customFilterSelectedValues.push(
                    new FilterItemTyped(CustomFilterByType.TEXT_TYPE, null, null, null, null, value)
                )
            }
        }
        console.log(`${NAME_SPACE}: event ${IUiSearchFilterComponent.APPLY_FILTER_COMMAND} fired with value ${value}`);

        this.applyFilter();
    }

    onDateFilterChanged(value: any, type:CustomFilterByType) {
        if(type) {
            const tmpDate:Date = new Date(value);
            tmpDate.setHours(0);
            tmpDate.setMinutes(0);
            tmpDate.setSeconds(0);
            value = tmpDate;

            const customFilterSelected=this.customFilterSelectedValues.find(elm => elm.type===type);
            if(customFilterSelected)
                customFilterSelected.inputValue=value;
            else {
                this.customFilterSelectedValues.push(
                    new FilterItemTyped(CustomFilterByType.TEXT_TYPE, null, null, null, null, value)
                )
            }
        }
        console.log(`${NAME_SPACE}: event ${IUiSearchFilterComponent.APPLY_FILTER_COMMAND} fired with value ${value}`);

        this.applyFilter();
    }

    onToggleFilterChanged(value: any, item:FilterItemTyped) {
        if(item.type) {
            const customFilterSelected=this.customFilterSelectedValues.find(elm => elm.type===item.type);
            if(customFilterSelected)
                customFilterSelected.inputValue=value;
            else {
                this.customFilterSelectedValues.push(
                    new FilterItemTyped(CustomFilterByType.TEXT_TYPE, null, null, null, null, value)
                )
            }
        }
        console.log(`${NAME_SPACE}: event ${IUiSearchFilterComponent.APPLY_FILTER_COMMAND} fired with value ${value}`);
        const toggleLabel = this.querySelector('#ion-toggle  > *');
        if(toggleLabel) toggleLabel.innerHTML = (item.inputValue) ? (item.label+': '+item.statusOn) : (item.label+': '+item.statusOff);

        this.applyFilter();
    }

    /* Protected scope */
    protected createRenderRoot(): Element | ShadowRoot {
        return this.renderRoot;
    }

    protected applyFilter() {
        this.dispatchEvent(new CustomEvent(
            IUiSearchFilterComponent.APPLY_FILTER_COMMAND,
            {
                detail: {
                    fieldName:this.customFilterSelectedValues[0].value,
                    inputValue:this.customFilterSelectedValues[0].inputValue,
                    fieldType:this.customFilterSelectedValues[0].type,
                    bubbles:true,
                    cancelable:true,
                    composed:true
                }
            })
        );
    }

    protected getTemplateFromFilterItemSelected(item: FilterItemTyped) {
        switch (item.type) {
            case CustomFilterByType.TEXT_TYPE:
                return filterByFreeTextTmpl(this);
            case CustomFilterByType.NUMBER_TYPE:
                return filterByNumberTmpl(this);
            case CustomFilterByType.DATE_TYPE:
                return filterByDateTmpl(this, item);
            case CustomFilterByType.TOGGLE_TYPE:
                return filterByToggleTmpl(this, item);
            default:
                return _``;
        }
    }

    /* Private scope */
    private isConsistentFiltering() {
        let types=[];
        let selectedTypes=1;

        this.customFilterSelectedValues.forEach((item) => {
            types.push(item.type);
        });

        let tmpType=types[0];
        types.forEach((item) => {
            if(item!==tmpType) {
                selectedTypes++;
                tmpType=item;
            }
        });

        return selectedTypes<2;
    }

    private createAlertInputs() {
        let inputs = [];

        // add element "all" in the case of single choice

        // REMOVED! SEARCH IN FIRST FILTER ITEM IF USER NOT CHOOSED

        // if (this.customFilterSelectionType === CustomFilterSelectionType.SINGLE_CHOISE) {
        //     inputs.push(
        //         {
        //             type: this.customFilterSelectionType,
        //             label: UiSearchFilterComponent.CUSTOM_FILTER_ALL_LABEL,
        //             value: UiSearchFilterComponent.CUSTOM_FILTER_ALL_VALUE,
        //             checked: this.customFilterSelectedValues.length == 0 || this.customFilterSelectedValues.includes(UiSearchFilterComponent.CUSTOM_FILTER_ALL_VALUE)
        //         }
        //     );
        // }

        this.customFilterItems.forEach((item) => {
            inputs.push(
                {
                    type: this.customFilterSelectionType,
                    label: item.label,
                    value: item.value,
                    checked: this.customFilterSelectedValues.includes(item)
                }
            );
        })

        return inputs;
    }

    private setFilterTypeSelected(data: any) {
        this.customFilterSelectedValues=[];
        if (data !== UiSearchFilterComponent.CUSTOM_FILTER_ALL_VALUE) {
            (this.customFilterSelectionType === CustomFilterSelectionType.SINGLE_CHOISE) ?
                this.customFilterSelectedValues.push(this.getItemFromData(data)) :
                data.forEach((item) => {
                    this.customFilterSelectedValues.push(this.getItemFromData(item));
                });
            this.customFilterSelectedValues.sort((a: FilterItemTyped, b: FilterItemTyped) => {
                if (a.type < b.type) return -1;
                if (a.type > b.type) return 1;
                return 0;
            });
        }
        if (this.isConsistentFiltering()) this.applyFilter();
        else {
            this.customFilterSelectedValues=[];
            alert('Filter values ​​not allowed');
        }
    }

    private getItemFromData(data: any) {
        const item = this.customFilterItems.find(item => item.value === data);
        const value = document.querySelector('ion-searchbar').value;
        item.inputValue = value;
        return item;
    }

    private getAlert() {
        const list = this.getElementsByTagName('ion-alert');
        if (list && list.length > 0) list[0].remove();

        return document.createElement('ion-alert');
    }

    protected render() {
        return _`
                <ion-grid style="background-color: #f6f6f6;" class="kta20-search-filter-bg">
                        <ion-row align-items-left>
                            ${(this.customFilterItems.length > 0) ? _`
                                <ion-col size="10">
                                    ${(this.customFilterSelectedValues.length > 0) ? this.getTemplateFromFilterItemSelected(this.customFilterSelectedValues[0]) : filterByFreeTextTmpl(this)}
                                </ion-col>
                                <ion-col size="2" no-padding text-left>
                                    <ion-button size="large" fill="clear" color="dark" @click="${async () => {await this.onCustumerFilter()}}">
                                        <ion-icon name="options"></ion-icon>
                                    </ion-button>
                                </ion-col>
                    `   : _`
                            <ion-col size="12" size-sm>
                                ${filterByFreeTextTmpl(this)}
                            </ion-col>
                    `   }
                        </ion-row>
                </ion-grid>
        `;
    }
}
